import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { Car } from "lucide-react";

export default function VehicleFinder({ variant = "hero" }) {
  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [engine, setEngine] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/vehicles/years").then(({ data }) => setYears(data.years || []));
  }, []);

  useEffect(() => {
    if (!year) return setMakes([]);
    api.get("/vehicles/makes", { params: { year } }).then(({ data }) => setMakes(data.makes || []));
  }, [year]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!year || !make) return;
    setLoading(true);
    navigate(`/vehicle-finder?year=${year}&make=${encodeURIComponent(make)}${engine ? `&engine=${encodeURIComponent(engine)}` : ""}`);
    setLoading(false);
  };

  const dark = variant === "hero";
  const surface = dark
    ? "bg-white/5 backdrop-blur-xl border-white/10 text-white"
    : "bg-white border-slate-200 text-slate-900";

  return (
    <form
      data-testid="vehicle-finder"
      onSubmit={submit}
      className={`rounded-2xl border ${surface} p-4 lg:p-5 grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_1fr_auto] items-center gap-3`}
    >
      <div className={`hidden md:flex items-center gap-2 ${dark ? "text-slate-300" : "text-slate-500"} font-mono text-xs uppercase tracking-widest`}>
        <Car className="w-4 h-4" /> Find by vehicle
      </div>
      <Select value={year} onValueChange={setYear}>
        <SelectTrigger data-testid="vf-year" className={dark ? "bg-white/5 border-white/10 text-white" : ""}>
          <SelectValue placeholder="Year" />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          {years.map((y) => (
            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={make} onValueChange={setMake} disabled={!year}>
        <SelectTrigger data-testid="vf-make" className={dark ? "bg-white/5 border-white/10 text-white" : ""}>
          <SelectValue placeholder="Make" />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          {makes.map((m) => (
            <SelectItem key={m.name} value={m.name}>{m.name} <span className="text-slate-400 text-xs ml-2">({m.count})</span></SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={engine} onValueChange={setEngine}>
        <SelectTrigger data-testid="vf-engine" className={dark ? "bg-white/5 border-white/10 text-white" : ""}>
          <SelectValue placeholder="Engine (optional)" />
        </SelectTrigger>
        <SelectContent>
          {["1.5L", "1.8L", "2.0L", "2.4L", "2.5L", "2.7L", "3.0L", "3.4L", "3.5L", "3.6L", "3.7L", "3.8L", "4.6L", "5.0L", "5.3L", "5.7L", "6.0L", "6.2L"].map((e) => (
            <SelectItem key={e} value={e}>{e}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button data-testid="vf-submit" type="submit" disabled={!year || !make || loading} className="bg-[#0055FF] hover:bg-[#0044CC] text-white h-10 px-6">
        Find Compatible Parts
      </Button>
    </form>
  );
}
